import { useCallback, useMemo } from "react";
import {
  Button,
  Icon,
  Menu,
  MenuCheckboxItem,
  MenuGroup,
  MenuGroupLabel,
  MenuPopup,
  MenuTrigger,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";

import { usePersistedState } from "#app/lib/hooks/use-persisted-state";
import { AnalyticsContext, OrgChartEvent, trackEvent } from "#app/lib/analytics";

const trackViewOption = (option: string, on: boolean) =>
  trackEvent({
    context: AnalyticsContext.OrgChart,
    event: OrgChartEvent.ToggleViewOption,
    data: { option, on },
  });

/** What a chart node card paints under the member's name. */
export interface OrgChartViewOptions {
  /** The job title (`user.title`). On by default. */
  showTitle: boolean;
  /** The member's workspace role(s). Off by default. */
  showRole: boolean;
}

const DEFAULT_VIEW_OPTIONS: OrgChartViewOptions = {
  showTitle: true,
  showRole: false,
};

// ONE key for every chart canvas (workspace org chart, team chart): the
// options describe how a member card reads, not which chart it sits on, so
// flipping "Role" on one canvas flips it on the other.
const STORAGE_KEY = "org-chart.view-options";

/** The card's fixed height for a set of options. The layout needs ONE
 *  height per chart and the library card centers its content in it. Name +
 *  title (14 + 2 + 12) or role label + name (10 + 2 + 14) both sit in 56;
 *  all three (10 + 2 + 14 + 2 + 12 = 40) step the box up to 72 so the
 *  content keeps the two-line card's 14px of air. A member missing a piece
 *  simply centers in the taller box. */
export const ORG_CHART_NODE_HEIGHT = { twoLine: 56, threeLine: 72 } as const;
export function orgChartNodeHeight(options: OrgChartViewOptions): number {
  return options.showTitle && options.showRole
    ? ORG_CHART_NODE_HEIGHT.threeLine
    : ORG_CHART_NODE_HEIGHT.twoLine;
}

/** The chart canvases' shared, per-browser view options. */
export function useOrgChartViewOptions(): {
  options: OrgChartViewOptions;
  setOption: <K extends keyof OrgChartViewOptions>(
    key: K,
    value: OrgChartViewOptions[K],
  ) => void;
} {
  const { state, update } = usePersistedState(STORAGE_KEY, DEFAULT_VIEW_OPTIONS);
  const setOption = useCallback(
    <K extends keyof OrgChartViewOptions>(key: K, value: OrgChartViewOptions[K]) =>
      update({ [key]: value } as Partial<OrgChartViewOptions>),
    [update],
  );
  return useMemo(() => ({ options: state, setOption }), [state, setOption]);
}

/**
 * The gear beside the chart's jump search: a "View options" menu of the card
 * toggles. Switch rows — each is a proposition to agree with, not one of a
 * set — that keep the menu open, so both can be flipped in one visit.
 */
export function OrgChartViewOptionsMenu({
  options,
  setOption,
}: ReturnType<typeof useOrgChartViewOptions>) {
  return (
    <Menu>
      {/* MenuTrigger's prop set is closed, so the TooltipTrigger sits inside
          its render chain and the hover handlers merge onto the button. */}
      <Tooltip>
        <MenuTrigger
          render={
            <TooltipTrigger
              render={
                <Button
                  aria-label="View options"
                  // The jump field beside it stands on the 28px first-line rung.
                  size="icon-sm"
                  variant="outline"
                />
              }
            />
          }
        >
          <Icon name="settings" size="sm" />
        </MenuTrigger>
        <TooltipPopup side="bottom">View options</TooltipPopup>
      </Tooltip>
      <MenuPopup align="end" width="action">
        <MenuGroup>
          <MenuGroupLabel>View options</MenuGroupLabel>
          <MenuCheckboxItem
            checked={options.showTitle}
            onCheckedChange={(checked) => {
              trackViewOption("showTitle", checked);
              setOption("showTitle", checked);
            }}
            variant="switch"
          >
            Title
          </MenuCheckboxItem>
          <MenuCheckboxItem
            checked={options.showRole}
            onCheckedChange={(checked) => {
              trackViewOption("showRole", checked);
              setOption("showRole", checked);
            }}
            variant="switch"
          >
            Role
          </MenuCheckboxItem>
        </MenuGroup>
      </MenuPopup>
    </Menu>
  );
}
